if (!customElements.get('pn-opinion-carousel')) {
  customElements.define('pn-opinion-carousel', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-opinion-track]');
      this.slides = Array.from(this.querySelectorAll('[data-opinion-slide]'));
      if (!this.track || !this.slides.length) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.viewport = this.querySelector('.pn-opinion-viewport');
      this.index = 0;
      this.physicalIndex = this.slides.length > 1 ? 1 : 0;
      this.interval = Math.max(4, Math.min(12, Number(this.dataset.interval) || 8)) * 1000;
      this.userPaused = this.dataset.autoplay !== 'true';
      this.editorPaused = false;
      this.busy = false;

      if (this.slides.length > 1) {
        this.track.prepend(this.makeClone(this.slides[this.slides.length - 1]));
        this.track.append(this.makeClone(this.slides[0]));
      }
      this.setAttribute('data-ready', '');
      this.position(false);
      this.updateState();
      this.track.addEventListener('transitionend', event => {
        if (event.target === this.track && event.propertyName === 'transform') this.finish();
      }, options);
      this.addEventListener('keydown', event => {
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
        if (event.key === ' ' || event.key === 'Spacebar') {
          if (this.motion.matches) return;
          event.preventDefault();
          this.userPaused = !this.userPaused;
          this.updateState();
          this.schedule();
          return;
        }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.viewport.addEventListener('pointerdown', event => {
        if (event.pointerType === 'touch') this.touchStart = { x: event.clientX, y: event.clientY };
      }, options);
      this.viewport.addEventListener('pointerup', event => {
        if (!this.touchStart) return;
        const x = event.clientX - this.touchStart.x;
        const y = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(x) > 35 && Math.abs(x) > Math.abs(y)) this.move(x < 0 ? 1 : -1);
      }, options);
      this.viewport.addEventListener('pointercancel', () => { this.touchStart = null; }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.finish();
        this.updateState();
        this.schedule();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (selected < 0) return;
        this.editorPaused = true;
        this.goTo(selected, false);
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        if (!this.slides.some(slide => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorPaused = false;
        this.schedule();
      }, options);
      this.schedule();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      cancelAnimationFrame(this.frame);
      this.querySelectorAll('[data-opinion-clone]').forEach(clone => clone.remove());
      this.removeAttribute('data-ready');
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      [clone, ...clone.querySelectorAll('*')].forEach(element => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('data-block-id');
        element.removeAttribute('data-opinion-slide');
      });
      clone.setAttribute('data-opinion-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      clone.querySelectorAll('img').forEach(image => {
        image.alt = '';
        image.loading = 'eager';
      });
      return clone;
    }

    move(direction) {
      if (this.busy || this.slides.length < 2) return;
      this.goTo(this.index + direction, !this.motion.matches);
    }

    goTo(nextIndex, animate) {
      clearTimeout(this.timer);
      clearTimeout(this.transitionTimer);
      this.index = ((nextIndex % this.slides.length) + this.slides.length) % this.slides.length;
      this.physicalIndex = this.slides.length > 1 ? (animate ? nextIndex + 1 : this.index + 1) : 0;
      this.busy = animate;
      this.position(animate);
      this.updateState();
      if (animate) {
        this.transitionTimer = window.setTimeout(() => this.finish(), 620);
      } else {
        this.schedule();
      }
    }

    position(animate) {
      cancelAnimationFrame(this.frame);
      this.track.toggleAttribute('data-instant', !animate);
      this.track.style.transform = `translateX(-${this.physicalIndex * 100}%)`;
      if (!animate) {
        this.track.getBoundingClientRect();
        this.frame = requestAnimationFrame(() => {
          if (this.controller) this.track.removeAttribute('data-instant');
        });
      }
    }

    finish() {
      if (!this.controller) return;
      clearTimeout(this.transitionTimer);
      this.busy = false;
      const physical = this.slides.length > 1 ? this.index + 1 : 0;
      if (physical !== this.physicalIndex) {
        this.physicalIndex = physical;
        this.position(false);
      }
      this.schedule();
    }

    updateState() {
      this.slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== this.index));
        slide.inert = index !== this.index;
      });
      this.dataset.current = String(this.index + 1);
      const action = this.motion.matches ? this.dataset.motionLabel :
        (this.userPaused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.viewport.setAttribute('aria-label', this.motion.matches ? action :
        `${action}. ${this.dataset.keyboardHint || ''}`);
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.controller || this.slides.length < 2 || this.busy || this.userPaused ||
        this.editorPaused || document.hidden || this.motion.matches) return;
      this.timer = window.setTimeout(() => this.move(1), this.interval);
    }
  });
}
